import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { apiCheckout } from "../lib/api";
import { Check, Star, Loader2 } from "lucide-react";

const PlanCard = ({ id, plan, highlight, popularLabel, perMonth, testid, onUpgrade, upgrading }) => (
  <div
    data-testid={testid}
    className={`relative flex flex-col rounded-3xl border p-7 ${
      highlight
        ? "border-sky-500/50 bg-gradient-to-b from-sky-500/[0.10] to-transparent shadow-[0_0_50px_-12px_rgba(56,189,248,0.5)]"
        : "border-slate-200 bg-slate-50"
    }`}
  >
    {highlight && (
      <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-[#1E3A8A] px-3 py-1 text-xs font-semibold text-white">
        <Star className="h-3 w-3 fill-slate-950" /> {popularLabel}
      </span>
    )}
    <div className="flex items-baseline gap-2">
      <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
    </div>
    <p className="mt-1 text-sm text-slate-500">{plan.tag}</p>
    <div className="mt-5 flex items-end gap-1">
      <span className="text-4xl font-bold tracking-tight text-slate-900">{plan.price}</span>
      {(id === "pro" || id === "family") && <span className="mb-1 text-sm text-slate-500">{perMonth}</span>}
    </div>
    <ul className="mt-6 flex-1 space-y-3">
      {plan.features.map((f, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
          <Check className={`mt-0.5 h-4 w-4 shrink-0 ${highlight ? "text-blue-700" : "text-emerald-400"}`} />
          {f}
        </li>
      ))}
    </ul>
    {id === "guest" || id === "free" ? (
      <Link
        to={id === "guest" ? "/check" : "/login"}
        data-testid={`pricing-cta-${id}`}
        className="mt-7 inline-flex items-center justify-center rounded-full bg-[#1E3A8A] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]"
      >
        {plan.cta}
      </Link>
    ) : id === "pro" || id === "family" ? (
      <button
        data-testid={`pricing-upgrade-${id}`}
        onClick={onUpgrade}
        disabled={upgrading}
        className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white transition-colors disabled:opacity-60 ${
          id === "family" ? "bg-[#0D9488] hover:bg-[#0f766e]" : "bg-[#1E3A8A] hover:bg-[#1D4ED8]"
        }`}
      >
        {upgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{plan.cta}
      </button>
    ) : (
      <button
        disabled
        className="mt-7 inline-flex cursor-not-allowed items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-500"
      >
        {plan.cta}
      </button>
    )}
  </div>
);

export default function Pricing() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const plans = t("pricing.plans");
  const perMonth = t("pricing.perMonth");
  const popular = t("pricing.mostPopular");
  const [upgrading, setUpgrading] = useState(false);

  const upgrade = async (plan) => {
    if (!user) { navigate("/login"); return; }
    setUpgrading(true);
    try {
      const { checkout_url } = await apiCheckout(window.location.origin, plan);
      window.location.href = checkout_url;
    } catch (_) { setUpgrading(false); }
  };

  return (
    <div className="tw-grid-bg min-h-screen pt-28 pb-24 sm:pt-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">{t("pricing.title")}</h1>
          <p className="mt-4 text-base text-slate-500">{t("pricing.subtitle")}</p>
        </div>

        <div className="mt-14 grid gap-5 lg:grid-cols-4">
          <PlanCard id="guest" plan={plans.guest} testid="pricing-plan-guest" perMonth={perMonth} />
          <PlanCard id="free" plan={plans.free} testid="pricing-plan-free" perMonth={perMonth} />
          <PlanCard id="pro" plan={plans.pro} testid="pricing-plan-pro" highlight popularLabel={popular} perMonth={perMonth} onUpgrade={() => upgrade("pro")} upgrading={upgrading} />
          <PlanCard id="family" plan={plans.family} testid="pricing-plan-family" perMonth={perMonth} onUpgrade={() => upgrade("family")} upgrading={upgrading} />
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-xs text-slate-500">{t("pricing.note")}</p>
      </div>
    </div>
  );
}
